import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import { FIXTURE_MATCH_TIME } from '../../constants';
import { Club, Match, MatchStatus } from '@repo/types';
import { ClubNameType } from '../../contracts';
import { isFutureMatch, parseMatchStatus } from '../../../utils/parseMatchStatus';
import { ImageVariant, Image } from '../../../common/components/Image';
import api from '@repo/api-endpoints';
import { Loader } from '../../../common/components/Loader/Loader';
import { Link } from 'react-router';

export type FixtureProps = {
  fixture: Match;
  showAttendance?: boolean;
  className?: string;
  nameType?: ClubNameType;
  style?: React.CSSProperties;
  isFixtureExpandable?: boolean;
};

type PlayerGoals = {
  player: string;
  goals: number[];
};

type PlayerRedCard = {
  player: string;
  redCard: number;
};

const sortGoals = (a: PlayerGoals, b: PlayerGoals) => {
  if (a.goals.length !== b.goals.length) {
    return a.goals.length > b.goals.length ? -1 : 1;
  }

  if (!a.goals[0]) {
    return -1;
  }

  if (!b.goals[0]) {
    return 1;
  }

  return a.goals[0] > b.goals[0] ? 1 : -1;
};

export const Fixture = ({
  fixture,
  showAttendance = false,
  nameType,
  className,
  isFixtureExpandable = false,
}: FixtureProps) => {
  const [fixtureDetails, setFixtureDetails] = useState<Match | null>(null);
  const [isLoadingFixtureDetails, setIsLoadingFixtureDetails] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const formattedTime = useMemo(() => {
    if (fixture.status === MatchStatus.NotKickedOff) {
      return fixture.time ? format(parseISO(`2024-01-01T${fixture.time}`), FIXTURE_MATCH_TIME) : 'N/A';
    }

    return '';
  }, [fixture]);

  const getClubName = (club: Club) => {
    switch (nameType) {
      case ClubNameType.ThreeLetterName:
        return club.threeLetterName ?? club.shortName ?? club.fullName;
      case ClubNameType.ShortName:
        return club.shortName ?? club.fullName;
      case ClubNameType.FullName:
        return club.fullName;
      case ClubNameType.NickName:
        return club.nickName ?? club.shortName ?? club.fullName;
      default:
        return club.shortName;
    }
  };

  const fetchFixtureDetails = useCallback(async () => {
    let match: Match | undefined;
    setIsLoadingFixtureDetails(true);
    try {
      const matchResponse = await fetch(`${api.endpoints.match}/${fixture.id}`, {
        headers: {
          // @ts-ignore fix vite import type
          'x-tenant-id': import.meta.env.VITE_TENANT_ID,
        },
      });

      if (matchResponse.ok) {
        match = await matchResponse.json();
      }
    } catch (err) {
      console.error('Error loading fixture on demand in fixture list: ', {
        err,
        endpoint: `${api.endpoints.match}/${fixture.id}`,
      });
    } finally {
      setIsLoadingFixtureDetails(false);
      if (match) {
        setFixtureDetails(match);
      }
    }
  }, [fixture]);

  const toggleFixtureDetails = useCallback(() => {
    setIsExpanded(!isExpanded);
    if (!fixtureDetails) {
      fetchFixtureDetails();
    }
  }, [fetchFixtureDetails, fixtureDetails, isExpanded]);

  const { isLiveMatch } = parseMatchStatus(fixture.status);

  const hasScores = ![MatchStatus.NotKickedOff, MatchStatus.Postponed, MatchStatus.Abandoned].includes(fixture.status);

  const playerStats = useMemo(() => {
    if (!fixtureDetails) {
      return null;
    }

    const homeTeamGoals: PlayerGoals[] = [];
    const homeTeamRedCards: PlayerRedCard[] = [];
    const awayTeamGoals: PlayerGoals[] = [];
    const awayTeamRedCards: PlayerRedCard[] = [];

    const homeTeamLineups = [...(fixtureDetails.lineups?.home ?? []), ...(fixtureDetails.subs?.home ?? [])];
    const awayTeamLineups = [...(fixtureDetails.lineups?.away ?? []), ...(fixtureDetails.subs?.away ?? [])];

    fixtureDetails.goals?.home?.forEach((currentItem) => {
      const player = homeTeamGoals.find(
        (homeTeamStat) => homeTeamStat.player === `${currentItem.player.firstName} ${currentItem.player.lastName}`
      );
      if (player) {
        player.goals.push(currentItem.minute);
      } else {
        homeTeamGoals.push({
          player: `${currentItem.player.firstName} ${currentItem.player.lastName}`,
          goals: [currentItem.minute],
        });
      }
    });

    homeTeamLineups.forEach((lineup) => {
      if (lineup.redCard) {
        homeTeamRedCards.push({
          player: `${lineup.player.firstName} ${lineup.player.lastName}`,
          redCard: lineup.redCard,
        });
      }
    });

    fixtureDetails.goals?.away?.forEach((currentItem) => {
      const player = awayTeamGoals.find(
        (awayTeamStat) => awayTeamStat.player === `${currentItem.player.firstName} ${currentItem.player.lastName}`
      );
      if (player) {
        player.goals.push(currentItem.minute);
      } else {
        awayTeamGoals.push({
          player: `${currentItem.player.firstName} ${currentItem.player.lastName}`,
          goals: [currentItem.minute],
        });
      }
    });

    awayTeamLineups.forEach((lineup) => {
      if (lineup.redCard) {
        awayTeamRedCards.push({
          player: `${lineup.player.firstName} ${lineup.player.lastName}`,
          redCard: lineup.redCard,
        });
      }
    });

    return {
      homeTeamGoals: homeTeamGoals.sort(sortGoals),
      homeTeamRedCards,
      awayTeamGoals: awayTeamGoals.sort(sortGoals),
      awayTeamRedCards,
    };
  }, [fixtureDetails]);

  const isExpandable = isFixtureExpandable && !isFutureMatch(fixture.status);

  return (
    <>
      <div
        className={cn('fixture-match', className, {
          'fixture-match--expanded': isExpanded,
        })}
      >
        <div
          className={cn('fixture-match__show-more-btn-container', {
            'fixture-match__show-more-btn-container--expandable': isExpandable,
          })}
        >
          {isExpandable && (
            <button className={'fixture-match__show-more-btn'} onClick={toggleFixtureDetails}>
              {isLoadingFixtureDetails ? (
                <Loader />
              ) : (
                <Image
                  variant={ImageVariant.SquareLogoSmall}
                  src={'web/chevron-right.svg'}
                  alt={'show match details'}
                  width={25}
                  height={25}
                  className={'fixture-match__show-more-icon'}
                />
              )}
            </button>
          )}
          <div className={'fixture-match__home-team-wrapper'}>
            <Link className={'fixture-match__team-link'} to={`/match/${fixture.id}`}>
              {getClubName(fixture.homeTeam as Club)}
              <Image
                src={fixture.homeTeam.logo ?? '/default-team-badge.webp'}
                alt={fixture.homeTeam.fullName}
                width={30}
                height={30}
                className={'fixture-match__team-logo'}
              />
            </Link>
            {isExpanded &&
              playerStats &&
              playerStats.homeTeamGoals?.map(({ player, goals }) => {
                return (
                  <p key={`${player}-goals`} className={'fixture-match__home-player-stats'}>
                    {player}
                    {goals.length > 0 && <div>({goals.map((goal) => `${goal}'`).join(', ')})</div>}
                  </p>
                );
              })}
            {isExpanded &&
              playerStats?.homeTeamRedCards?.map(({ player, redCard }) => {
                return (
                  <p key={`${player}-goals`} className={'fixture-match__home-player-stats'}>
                    {player}
                    <div className={'match-overview__red-card'} />({redCard}')
                  </p>
                );
              })}
          </div>
        </div>
        <Link className={'fixture-match__score-link'} to={`/match/${fixture.id}`}>
          {hasScores && (
            <div
              className={cn('fixture-match__score', {
                'fixture-match__score--live': isLiveMatch,
              })}
            >
              <div>
                <span>{fixture.score.current.home}</span>
                <span> - </span>
                <span>{fixture.score.current.away}</span>
              </div>
              {fixture.score.penalties && (
                <span className="fixture-match__score__ht fixture-match__score__status">
                  {fixture.score.penalties.home} P {fixture.score.penalties.away}
                </span>
              )}
              {fixture.status === MatchStatus.HalfTime && (
                <span className="fixture-match__score__ht fixture-match__score__status">HT</span>
              )}
              {fixture.status === MatchStatus.FirstHalfOfExtraTime && (
                <span className="fixture-match__score__fh_et fixture-match__score__status">ET</span>
              )}
              {fixture.status === MatchStatus.HalfTimeInExtraTime && (
                <span className="fixture-match__score__ht_et fixture-match__score__status">HT</span>
              )}
              {fixture.status === MatchStatus.SecondHalfOfExtraTime && (
                <span className="fixture-match__score__sh_et fixture-match__score__status">ET</span>
              )}
              {fixture.status === MatchStatus.FullTimeAfterExtraTime && (
                <span className="fixture-match__score__ft_et fixture-match__score__status">AET</span>
              )}
              {fixture.status === MatchStatus.PenaltyShootOut && (
                <span className="fixture-match__score__ps fixture-match__score__status">Pens</span>
              )}
            </div>
          )}
          {fixture.status === MatchStatus.Abandoned && <div className="fixture-match__time">A - A</div>}
          {fixture.status === MatchStatus.Postponed && <div className="fixture-match__time">P - P</div>}
          {fixture.status === MatchStatus.NotKickedOff && <div className="fixture-match__time">{formattedTime}</div>}
        </Link>
        <div className={'fixture-match__away-team-wrapper'}>
          <div className="fixture-match__team-attendance-wrapper">
            <Link className={'fixture-match__team-link'} to={`/match/${fixture.id}`}>
              <Image
                src={fixture.awayTeam.logo ?? '/default-team-badge.webp'}
                alt={fixture.awayTeam.fullName}
                width={30}
                height={30}
                className={'fixture-match__team-logo'}
              />
              {getClubName(fixture.awayTeam as Club)}
            </Link>
            {isExpandable ? (
              <button className={'fixture-match__show-more-btn'} onClick={toggleFixtureDetails}>
                {showAttendance && fixture.attendance && fixture.attendance > 0 ? (
                  <p className={'fixture-match__attendance'}>
                    Att: <span>{fixture.attendance}</span>
                  </p>
                ) : null}
              </button>
            ) : (
              <>
                {showAttendance && fixture.attendance && fixture.attendance > 0 ? (
                  <p className={'fixture-match__attendance'}>
                    Att: <span>{fixture.attendance}</span>
                  </p>
                ) : null}
              </>
            )}
          </div>
          {isExpanded &&
            playerStats &&
            playerStats.awayTeamGoals?.map(({ player, goals }) => {
              return (
                <p key={`${player}-goals`} className={'fixture-match__away-player-stats'}>
                  {player}
                  {goals.length > 0 && <div>({goals.map((goal) => `${goal}'`).join(', ')})</div>}
                </p>
              );
            })}
          {isExpanded &&
            playerStats?.awayTeamRedCards?.map(({ player, redCard }) => {
              return (
                <p key={`${player}-goals`} className={'fixture-match__away-player-stats'}>
                  {player}
                  <div className={'match-overview__red-card'} />({redCard}')
                </p>
              );
            })}
        </div>
      </div>
      {fixture.info && (
        <Link className={'fixture-match__team-link'} to={`/match/${fixture.id}`}>
          <div className="fixture-match__info" dangerouslySetInnerHTML={{ __html: fixture.info }} />
        </Link>
      )}
    </>
  );
};
